import { Component } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { EzDialogComponent } from '@template-ui/components/ez-dialog/ez-dialog.component';
//import moment from 'moment';
import { format, isValid } from 'date-fns';
import { RulesEngine } from '@template-ui/rules/rules-engine';
import { EzButtonComponent } from "@template-ui/components/ez-button/ez-button.component";
import { EzTextService } from '@template-ui/services/ez-text.service';
import { CommonModule } from "@angular/common";

// Interface for field configuration
interface TitleFieldConfig {
  field: string;
  type?: 'string' | 'date' | 'number';
}

/**
* ButtonCellRenderer (Single Button)
* Use when you need:
*
* - One primary action per grid cell
* - Complex business logic with rules-based permissions
* - Built-in confirmation dialogs with customizable messages
* - Role-based access control integration
* - Advanced conditional logic for enabling/disabling buttons
* - Key Features
* - ❌ Rules engine integration for permission checking (trying to remove this)
* - ✅ Built-in confirmation dialog with dynamic messaging
* - ✅ Support for titleField and titleFields for custom confirmation text
* - ✅ Role-based evaluation with comprehensive context
* - ✅ Advanced conditional visibility and enablement
* - ✅ Per-row accessible name through the `ariaLabel` param (`{{field}}` tokens resolve against the row)
*
* The visible label is the same for every row, so a screen-reader user hearing "Open, button" ten
* times cannot tell the rows apart (WCAG 2.2 criterion 2.4.4). Give the button a per-row accessible
* name that starts with the visible text (criterion 2.5.3 Label in Name):
* ```ts
* cellRendererParams: { label: 'Open', ariaLabel: 'Open {{displayName}}' } // aria-label "Open Gregory Holt"
* ```
*
* @deprecated - Use only if ButtonsCellRender in EZ Lib doesn't do what you want, trying to merge.
*/
@Component({
  selector: 'app-button-cell-renderer',
  templateUrl: './button-cell-renderer.component.html',
  styleUrls: ['./button-cell-renderer.component.scss'],
  imports: [EzButtonComponent, CommonModule]
})
export class ButtonCellRenderer implements ICellRendererAngularComp {
  params!: any;
  label!: string;
  /** Accessible name resolved from the `ariaLabel` param against the row data; absent when the param is not given. */
  ariaLabel?: string;
  isEnabled: boolean = true;
  hideButton = false;
  buttonWidth: string | null = null;
  isDisabledByRules = false;

  constructor(private dialog: MatDialog, private ezText: EzTextService) {}

  agInit(params: any): void {
    this.params = params;
    this.label = this.params.label || this.params.value;
    this.buttonWidth = this.params.buttonWidth
    this.resolveAriaLabel();

    this.checkRulesDisabled();

    this.enabled();
    this.buttonVisible();
  }



  get isDisabled(): boolean {
    return this.isDisabledByRules || !this.isEnabled;
  }

  refresh(params?: any): boolean {
    this.params = params;
    this.label = this.params.label || this.params.value;
    this.resolveAriaLabel();
    this.enabled();
    this.buttonVisible();
    this.checkRulesDisabled();

    return true;
  }

  onClick($event: any) {

    if (this.isDisabledByRules || !this.isEnabled) {
      $event.preventDefault();
      return;
    }

    if (this.params.onClick instanceof Function) {
      if (this.params.type === 'Confirm') {
        this.confirmAction($event);
      } else {
        const params = {
          event: $event,
          rowData: this.params.node.data,
        };
        this.params.onClick(params);
      }
    }
  }

  enabled($event?: any) {
    if (!this.isDisabledByRules && this.params.enabled instanceof Function) {
      const params = {
        event: $event,
        rowData: this.params.node.data,
      };

      this.isEnabled = this.params.enabled(params);
    }
  }

  confirmAction($event: any) {

    if (this.isDisabledByRules) {
      return;
    }

    this.dialog.open(EzDialogComponent, {
      width: '35%',
      data: {
        heading: this.params.confirmDialogHeader ?? `Confirm`,
        question: this.buildConfirmationMessage(),
        headerStyle: 'warning',
        questionAlignment: 'center',
        buttons: [
          {
            text: 'No',
            action: (ref: MatDialogRef<EzDialogComponent>) => ref.close(),
          },
          {
            text: 'Yes',
            action: (ref: MatDialogRef<EzDialogComponent>) => {
              const params = {
                event: $event,
                rowData: this.params.node.data,
              };
              this.params.onClick(params);
              ref.close();
            },
          },
        ],
      },
    });
  }

  buttonVisible($event?: any) {
    if (this.params.buttonCondition instanceof Function) {
      const params = {
        event: $event,
        rowData: this.params.node.data,
      };

      this.hideButton = this.params.buttonCondition(params);
    }
  }

/**
 * Evaluates whether the button should be disabled based on role-based business rules and row-specific data.
 *
 * This method performs a comprehensive evaluation using the  rules engine to determine
 * if the current user has permission to perform the action represented by this button.
 * The evaluation considers user roles, case data, row-specific context, and action-specific rules.
 *
 * **Evaluation Process:**
 * 1. **Prerequisites Check** - Validates that rulesConfig and actionName are provided
 * 2. **Role-Based Evaluation** - Uses rules engine with multiple configs per role
 * 3. **Context Assembly** - Combines global data with row-specific data for comprehensive context
 * 4. **Action-Specific Rules** - Applies rules that target the specific action being evaluated
 * 5. **Result Assignment** - Sets `isDisabledByRules` property based on evaluation result
 *
 * @private
 * @returns `void` - Results are stored in the `isDisabledByRules` property
 *
 * @example
 * ```typescript
 * // This method is typically called automatically during component lifecycle:
 * // - agInit() - Initial setup
 * // - refresh() - When cell data changes
 *
 * // Example button configuration in column definition:
 * {
 *   field: "Delete",
 *   cellRenderer: ButtonCellRenderer,
 *   cellRendererParams: {
 *     label: "Delete Offense",
 *     onClick: this.deleteOffenseRow.bind(this),
 *     actionName: 'deleteOffense', //  Used by this method
 *     rulesConfig: this.agGridRulesConfig$ //  Contains rules and data
 *   }
 * }
 *
 * // Role-based evaluation examples:
 * //
 * // IRSD_ANALYST user with 'deleteOffense' action:
 * // - Action in exceptions list → Always disabled (true)
 * //
 * // CLMNCY_INV user with 'deleteOffense' on assigned active case:
 * // - No role-based restrictions → Uses default rules → Enabled (false)
 * //
 * // Any user with 'deleteOffense' on completed case:
 * // - Default rules apply → Case complete → Disabled (true)
 * ```
 *
 * @remarks
 * **Required Parameters:**
 * - `this.params.rulesConfig` - Must contain the rules configuration and evaluation data
 * - `this.params.actionName` - Must specify the action being evaluated (e.g., 'deleteOffense')
 *
 * **Context Data Structure:**
 * The method assembles a comprehensive evaluation context containing:
 * - **Global Data** - Case information, user ID, assignment status, etc.
 * - **Row Data** - Specific row being acted upon (offense details, etc.)
 * - **User Roles** - Current user's roles for role-based rule evaluation
 *
 * @see {@link RulesEngine.evaluateConfigurationForAction} for action evaluation logic
 * @see {@link AgGridRulesConfig} for rules configuration structure
 * @see {@link ButtonCellRenderer.isDisabled} for how this result is used
 *
 */
  private checkRulesDisabled(): void {
  if (this.params.rulesConfig && this.params.actionName) {
    this.isDisabledByRules = RulesEngine.evaluateConfigurationForAction(
      this.params.rulesConfig.rulesConfig,
      this.params.actionName,
      {
        ...this.params.rulesConfig.data,
        rowData: this.params.node?.data,
        userRoles: this.params.rulesConfig.data?.userRoles || []
      }
    );
  } else {
    this.isDisabledByRules = false;
  }
}

  /**
   * Resolves the optional `ariaLabel` param template (for example `Open {{displayName}}`) against the
   * row data, the same way the library ButtonsCellRenderer resolves its button text tokens.
   * Leaves the accessible name to the visible label when the param is not given.
   */
  private resolveAriaLabel(): void {
    const template = this.params?.ariaLabel;
    if (typeof template !== 'string' || template.trim() === '') {
      this.ariaLabel = undefined;
      return;
    }

    const rowData = this.params.data ?? this.params.node?.data ?? {};
    this.ariaLabel = this.ezText.resolveTemplate(template, rowData);
  }

  /**
   * Builds the confirmation message based on titleField(s) configuration
   */
  private buildConfirmationMessage(): string {
    if (this.params.titleField && typeof this.params.titleField === 'string') {
      const value = this.params.node.data[this.params.titleField];
      return `Are you sure you want to delete ${value || ''}?`;
    }

    if (this.params.titleFields && Array.isArray(this.params.titleFields)) {
      const formattedValues = this.params.titleFields
        .map((fieldConfig: TitleFieldConfig) => {
          const value = this.params.node.data[fieldConfig.field];
          return this.formatFieldValue(value, fieldConfig);
        })
        .filter((value: string) => value.trim() !== '') // Remove empty values
        .join(' ');

      return formattedValues
        ? `Are you sure you want to delete ${formattedValues}?`
        : 'Are you sure you want to delete?';
    }

    return 'Are you sure you want to delete?';
  }

  /**
   * Formats a field value based on its type configuration using date-fns for dates
   */
  private formatFieldValue(value: any, config: TitleFieldConfig): string {
    if (value == null || value === '') {
      return '';
    }

    switch (config.type) {
      case 'date':
        return this.formatDateWithDateFns(value);
      case 'number':
        return typeof value === 'number' ? value.toString() : String(value);
      case 'string':
      default:
        return String(value);
    }
  }

  /**
   * Formats a date value using Moment.js with default format NO NEED FOR MOMENT, WE ALREADY HAVE date-fns.
   */
  // private formatDateWithMoment(value: any): string {
  //   try {
  //     const momentDate = moment(value);

  //     if (!momentDate.isValid()) {
  //       return String(value);
  //     }

  //     return momentDate.format('MM/DD/YY YY');
  //   } catch (error) {
  //     return String(value);
  //   }
  // }



  private formatDateWithDateFns(value: any): string {
    try {
      const date = new Date(value);

      if (!isValid(date)) {
        return String(value);
      }

      return format(date, 'MM/dd/yyyy');
    } catch (error) {
      return String(value);
    }
  }

}
